import { ANCHOR_KIND, anchorPayload } from './anchors';
import { WAVE_STATUS, diffRewards } from './constants';
import { fromDatetimeLocal } from './waveEditPayload';

/**
 * The POST /create and PUT /update bodies for an anchor-scheduled wave — pure, so the rules the
 * API enforces can be reasoned about (and tested) away from the editor.
 *
 * Four rules do all the work here, and every one of them is a 400 if broken:
 *
 *  1. `openAnchor` and `closeAnchor` go together or not at all.
 *  2. Anchors may NOT accompany startMinute / endMinute / startsAt / endsAt. Two descriptions of
 *     one schedule is rejected outright, not merged.
 *  3. `duration` is invalid on the opening edge.
 *  4. A RUNNING wave refuses ANCHORS ENTIRELY — see `liveClosePayload` below, which is the part
 *     of this most easily got wrong.
 */

/** Anchors are Game Day only; a Non-Game Day wave IS its startsAt/endsAt window. */
export const buildCreatePayload = ({ teamId, gameId, openDraft, closeDraft, target, rewards, baseRewards }) => {
  const payload = {
    teamId,
    mode: 'game_day',
    gameId,
    openAnchor: anchorPayload(openDraft, fromDatetimeLocal),
    closeAnchor: anchorPayload(closeDraft, fromDatetimeLocal)
  };

  // An empty target is not 0 — it means "inherit the channel's", which is expressed by omitting
  // the field rather than sending anything.
  if (target !== '' && target != null) payload.target = Number(target);

  const changed = baseRewards ? diffRewards(baseRewards, rewards) : rewards;
  if (changed && Object.keys(changed).length) payload.rewards = changed;

  return payload;
};

/**
 * Whether an ACTIVE wave's close can be expressed at all.
 *
 * PUT /update refuses an anchor pair on a running wave outright — the guard fires on the pair,
 * not on whether the open actually changed, because anchors describe the WHOLE schedule and
 * sending them is by definition a new start. So a live wave's close has to travel on the legacy
 * fields, and only the two anchor kinds that HAVE a legacy field can be used:
 *
 *   absolute      → endsAt
 *   match_minute  → endMinute
 *
 * A `match_event` or `duration` close has no legacy representation (`anchorsToLegacy` nulls both),
 * so there is nothing to send and the editor has to say so rather than build a body the API will
 * reject. This is a real gap in what a live wave can be re-pointed at, not a client shortcut.
 */
export const LIVE_CLOSE_KINDS = [ANCHOR_KIND.ABSOLUTE, ANCHOR_KIND.MATCH_MINUTE];

export const liveCloseUnsupported = (closeDraft) =>
  LIVE_CLOSE_KINDS.includes(closeDraft?.kind)
    ? null
    : 'A running wave’s close has to be a date & time or a match minute — the API cannot accept an event or duration close once a wave has opened. Cancel it and enable a new one to use those.';

/**
 * The PUT /admin/fanwave/update body.
 *
 * WHICH wave: always `waveId`. `gameId` is ambiguous the moment a match holds more than one wave
 * and the API answers 409 asking for the waveId, which is exactly the situation the timeline
 * exists to create.
 *
 * Returns `{ payload, changed, targetChanged, blocked }`. `blocked` is a message when the edit
 * cannot be expressed at all (a terminal wave, or a live wave whose close kind has no legacy
 * field); `changed` lists the real changes, because the API needs at least one.
 */
export const buildAnchorUpdatePayload = ({
  wave,
  status,
  openDraft,
  closeDraft,
  original,
  target,
  rewards,
  baseRewards
}) => {
  const o = original || {};

  if (
    status === WAVE_STATUS.COMPLETED ||
    status === WAVE_STATUS.CANCELLED ||
    status === WAVE_STATUS.EXPIRED
  ) {
    return {
      payload: null,
      changed: [],
      targetChanged: false,
      blocked:
        'This wave has already finished — the API answers 409 for an edit. Delete it instead.'
    };
  }

  const isLive = status === WAVE_STATUS.ACTIVE;
  const payload = { teamId: wave.teamId, waveId: wave.waveId };

  const nextOpen = anchorPayload(openDraft, fromDatetimeLocal);
  const nextClose = anchorPayload(closeDraft, fromDatetimeLocal);

  if (isLive) {
    const unsupported = liveCloseUnsupported(closeDraft);
    // Only a close that MOVED is worth blocking on — an untouched unsupported close is just the
    // shape the wave already runs on, and the admin may only be changing the target.
    const closeMoved = JSON.stringify(nextClose) !== JSON.stringify(o.close);
    if (unsupported && closeMoved) {
      return { payload: null, changed: [], targetChanged: false, blocked: unsupported };
    }
    if (closeMoved && !unsupported) {
      // The legacy field for this close kind, and ONLY it. Sending the other member of the edge
      // alongside is a 400 ("send one, not both").
      if (closeDraft.kind === ANCHOR_KIND.ABSOLUTE) {
        payload.endsAt = fromDatetimeLocal(closeDraft.at);
      } else {
        payload.endMinute = Number(closeDraft.minute);
      }
    }
  } else {
    const openMoved = JSON.stringify(nextOpen) !== JSON.stringify(o.open);
    const closeMoved = JSON.stringify(nextClose) !== JSON.stringify(o.close);
    if (openMoved || closeMoved) {
      // Both edges together, always: an open with no close would never end, and the API's `.and()`
      // refuses a lone one. They also REPLACE the schedule rather than merging edge by edge, so
      // the unchanged edge has to be re-sent as it stands.
      payload.openAnchor = nextOpen;
      payload.closeAnchor = nextClose;
    }
  }

  const targetChanged = normalizeTarget(target) !== normalizeTarget(o.target);
  if (targetChanged) {
    const next = normalizeTarget(target);
    // An emptied field returns the wave to the channel default, which is an explicit null.
    payload.target = next == null ? null : next;
    // Changing a LIVE wave's target needs the admin to confirm — fans are already tapping toward
    // the old one.
    if (isLive) payload.confirm = true;
  }

  const rewardsDiff = diffRewards(o.rewards, rewards);
  if (rewardsDiff) payload.rewards = baseRewards ? diffRewards(baseRewards, rewards) || {} : rewardsDiff;

  const changed = Object.keys(payload).filter(
    (k) => !['teamId', 'waveId', 'gameId', 'confirm'].includes(k)
  );

  return { payload, changed, targetChanged, blocked: null };
};

/** '' / null → null (inherit the channel default); anything else → a number. */
const normalizeTarget = (value) => {
  if (value === '' || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};
